import React from 'react';
import { FormControl, InputLabel, MenuItem, Select, Stack, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';

export type RangePreset = 'today' | '7d' | '30d' | 'custom';
export interface UsageFilterState { preset: RangePreset; from: string; to: string; projectId: string; userId: string }

const iso = (d: Date) => {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const presetRange = (preset: RangePreset): { from: string; to: string } => {
  const to = new Date();
  const from = new Date();
  if (preset === '7d') from.setDate(to.getDate() - 6);
  if (preset === '30d') from.setDate(to.getDate() - 29);
  return { from: iso(from), to: iso(to) };
};

export const defaultFilters = (): UsageFilterState => ({ preset: '7d', ...presetRange('7d'), projectId: '', userId: '' });

interface Props {
  value: UsageFilterState;
  onChange: (next: UsageFilterState) => void;
  projects: Array<{ id: string; name: string }>;
  isAdmin: boolean;
  selfUserId: string;
}

export const UsageFilters: React.FC<Props> = ({ value, onChange, projects, isAdmin, selfUserId }) => (
  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
    <ToggleButtonGroup size="small" exclusive value={value.preset}
      onChange={(_, preset: RangePreset | null) => {
        if (!preset) return;
        onChange(preset === 'custom' ? { ...value, preset } : { ...value, preset, ...presetRange(preset) });
      }}>
      <ToggleButton value="today">Today</ToggleButton>
      <ToggleButton value="7d">7 days</ToggleButton>
      <ToggleButton value="30d">30 days</ToggleButton>
      <ToggleButton value="custom">Custom</ToggleButton>
    </ToggleButtonGroup>
    {value.preset === 'custom' && (
      <>
        <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={value.from}
          onChange={(e) => onChange({ ...value, from: e.target.value })} />
        <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={value.to}
          onChange={(e) => onChange({ ...value, to: e.target.value })} />
      </>
    )}
    <FormControl size="small" sx={{ minWidth: 180 }}>
      <InputLabel id="usage-project">Project</InputLabel>
      <Select labelId="usage-project" label="Project" value={value.projectId}
        onChange={(e) => onChange({ ...value, projectId: String(e.target.value) })}>
        <MenuItem value="">All projects</MenuItem>
        <MenuItem value="unassigned">Unassigned</MenuItem>
        {projects.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
      </Select>
    </FormControl>
    {isAdmin && (
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel id="usage-user">User</InputLabel>
        <Select labelId="usage-user" label="User" value={value.userId}
          onChange={(e) => onChange({ ...value, userId: String(e.target.value) })}>
          <MenuItem value="">All users</MenuItem>
          <MenuItem value={selfUserId}>Me</MenuItem>
        </Select>
      </FormControl>
    )}
  </Stack>
);

export default UsageFilters;
